import React from "react";
import { FlexColumn, FlexRow } from "@/ui/FlexContainer/FlexContainer";
import Typography from "@/ui/Typography/Typography";
import Button from "@/ui/Button/Button";
import { ReferalI } from "@/types/referals";
import classNames from "classnames";
import {
  fullName,
  declinateOrders,
  getDaysText,
  calculateDaysLeft,
} from "@/utils/helpers";
import { openTelegramUrl, telegramChatLink } from "@/utils/telegram";
import { dataTrigger } from "@/analytics";
import cls from "./styles.module.scss";

interface ReferalCardProps {
  referal: ReferalI;
}

const referralSourceLabels: Record<string, string> = {
  sravni_dohod: "Сравни Доход",
};

export const ReferalCard = ({ referal }: ReferalCardProps) => {
  const createdDate = referal?.driver?.created_date
    ? new Date(referal.driver.created_date)
        .toLocaleDateString("ru-RU", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
        .replace(" г.", "")
    : "";

  const maxRidesAmount = () => {
    if (
      !referal?.ref_details?.aim_actions ||
      referal?.ref_details?.aim_actions?.length === 0
    )
      return 1;

    return Math.max(
      ...referal.ref_details.aim_actions.map((item) => item?.rides_amount)
    );
  };

  const ridesAmount = referal?.ref_details?.referral_program_rides_amount ?? 0;

  const isConditionsCompleted = ridesAmount >= maxRidesAmount();

  const btnLabel = ridesAmount > 0 ? "Напомнить" : "Помочь";
  const followupTrigger =
    ridesAmount > 0
      ? "ref.referrals.remind_goal"
      : "ref.referrals.support_goal";

  const onClick = (attachText?: boolean) => {
    const message = attachText
      ? referal?.native_followup?.text || "Богатый курьер"
      : "";
    const chatLink = telegramChatLink(referal?.telegram_user, message);

    openTelegramUrl(
      chatLink ?? `https://t.me/share/url?url=${encodeURIComponent(message)}`
    );
  };

  const daysLeft = calculateDaysLeft(
    referal?.ref_details?.referral_offer_end_date_for_driver ??
      referal?.referral_deadline
  );
  const hasGoals = (referal?.ref_details?.aim_actions?.length ?? 0) > 0;
  const chatLink = telegramChatLink(referal?.telegram_user);
  const contactLabel = referal?.telegram_user?.username
    ? `@${referal.telegram_user.username}`
    : referal?.telegram_user?.phone_number ?? "";
  const referralSourceLabel = referal.referral_source
    ? referralSourceLabels[referal.referral_source] ?? referal.referral_source
    : null;

  if (referal.status === "unregistered")
    return (
      <FlexColumn gap={14} className={cls.referalCard}>
        <FlexRow justifyContent="space-between" alignItems="flex-start">
          <FlexColumn
            gap={3}
            alignItems="flex-end"
            role="button"
            {...dataTrigger("ref.referrals.username")}
            onClick={() => {
              if (chatLink) onClick();
            }}
          >
            <FlexRow>
              <Typography className={cls.username}>{contactLabel}</Typography>
              {chatLink && <div className={cls.arrowBtn}>→</div>}
            </FlexRow>

            <Typography className={cls.desc}>
              Не зарегистририрован в Парке
            </Typography>
          </FlexColumn>

          {referralSourceLabel && (
            <div className={classNames(cls.service, cls.referralSource)}>
              <Typography>{referralSourceLabel}</Typography>
            </div>
          )}
        </FlexRow>

        <Button
          trigger="ref.referrals.remind_registration"
          label="Напомнить про регистрацию"
          onClick={() => {
            onClick(true);
          }}
        />
      </FlexColumn>
    );

  return (
    <FlexColumn gap={14} className={cls.referalCard}>
      <FlexColumn
        gap={8}
        role="button"
        {...dataTrigger("ref.referrals.username")}
        onClick={() => {
          onClick();
        }}
      >
        <FlexRow justifyContent="space-between" alignItems="flex-start">
          <FlexColumn gap={3}>
            <FlexRow gap={8} className={cls.nameLink}>
              <Typography>
                {fullName(
                  referal?.driver?.first_name,
                  referal?.driver?.middle_name,
                  referal?.driver?.last_name
                )}
              </Typography>

              <div className={cls.arrowBtn}>→</div>
            </FlexRow>

            <Typography className={cls.desc}>
              В парке с {createdDate}
            </Typography>
          </FlexColumn>

          {(referal?.driver?.car_category_upd || referal?.referral_source) && (
            <FlexColumn
              gap={6}
              className={cls.serviceList}
              alignItems="flex-end"
            >
              {referal?.driver?.car_category_upd && (
                <div className={cls.service}>
                  <Typography>{referal.driver.car_category_upd}</Typography>
                </div>
              )}
              {referralSourceLabel && (
                <div className={classNames(cls.service, cls.referralSource)}>
                  <Typography>{referralSourceLabel}</Typography>
                </div>
              )}
            </FlexColumn>
          )}
        </FlexRow>

        {(referal?.telegram_user?.username ||
          referal?.telegram_user?.phone_number) && (
          <FlexRow gap={8}>
            {referal?.telegram_user?.phone_number && (
              <div className={cls.tgInfoBlock}>
                {referal.telegram_user.phone_number}
              </div>
            )}
            {referal?.telegram_user?.username && (
              <div className={cls.tgInfoBlock}>
                @{referal.telegram_user.username}
              </div>
            )}
          </FlexRow>
        )}
      </FlexColumn>

      {(referal.status === "active" || referal.status === "completed") && (
        <FlexRow justifyContent="space-between">
          {!isConditionsCompleted && daysLeft > 0 && (
            <div className={cls.timing}>
              🕐 {getDaysText(daysLeft)} на выполнение
            </div>
          )}

          {hasGoals && (
            <Typography className={cls.orders}>
              <span>
                {ridesAmount >= maxRidesAmount()
                  ? maxRidesAmount()
                  : ridesAmount}
                /{maxRidesAmount()}
              </span>{" "}
              заказов
            </Typography>
          )}
        </FlexRow>
      )}

      {referal?.ref_details && (
        <FlexColumn gap={15}>
          {referal.ref_details.aim_actions.map((elem, index) => {
            const isStepPassed = ridesAmount >= elem?.rides_amount;
            const stepAmount = elem.effective_step_1_amount;

            return (
              <FlexRow
                key={elem.id}
                justifyContent="space-between"
                className={cls.refCardBlock}
              >
                <FlexRow gap={12}>
                  <div
                    className={classNames(
                      cls.circle,
                      isStepPassed ? cls.circleActive : undefined
                    )}
                  >
                    {index < referal?.ref_details.aim_actions.length - 1 && (
                      <div className={cls.connectCirclesBlock} />
                    )}
                  </div>

                  <Typography
                    className={isStepPassed ? cls.activeStep : cls.step}
                  >
                    +{stepAmount.toLocaleString("ru-RU")} рублей
                  </Typography>
                </FlexRow>
                <Typography className={cls.step}>
                  {declinateOrders(elem?.rides_amount || 0)}
                </Typography>
              </FlexRow>
            );
          })}
        </FlexColumn>
      )}

      {!isConditionsCompleted && referal.status === "active" && (
        <Button
          trigger={followupTrigger}
          label={btnLabel}
          onClick={() => {
            onClick(true);
          }}
        />
      )}
    </FlexColumn>
  );
};
